import React, { useEffect, useState } from 'react';
import { LogModel } from '../../../../interfaces/log.interface';
import { fetchErrorLogs } from '../../ErrorLogs/services/ErrorLogs.service';
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner';
import ErrorLogsTable from '../../ErrorLogs/components/ErrorLogsTable';
import PaginationButtons from '../../../../components/Common/Pagination';

const ERROR_CODE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Error Codes' },
  { value: 'PROVIDER_ERROR', label: 'Provider Error' },
  { value: 'PROVIDER_TIMEOUT', label: 'Provider Timeout' },
  { value: 'PROVIDER_CONNECTION_ERROR', label: 'Connection Error' },
  { value: 'PROVIDER_RATE_LIMITED', label: 'Rate Limited' },
  { value: 'CONTENT_FILTERED', label: 'Content Filtered' },
  { value: 'WEBHOOK_DELIVERY_FAILED', label: 'Webhook Failed' },
  { value: 'GCS_UPLOAD_FAILED', label: 'GCS Upload Failed' },
  { value: 'PROMPT_GENERATION_FAILED', label: 'Prompt Gen Failed' },
  { value: 'JOB_ABANDONED', label: 'Job Abandoned' },
  { value: 'IMAGE_DOWNLOAD_FAILED', label: 'Image Download Failed' },
  { value: 'UNKNOWN', label: 'Unknown' },
];

const SEVERITY_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Errors & Warnings' },
  { value: 'error', label: 'Errors only' },
  { value: 'warning', label: 'Warnings only' },
];

interface BookErrorsModalProps {
  bookId: string;
  onClose: () => void;
}

const BookErrorsModal: React.FC<BookErrorsModalProps> = ({ bookId, onClose }) => {
  const [loader, setLoader] = useState(true);
  const [errorLogs, setErrorLogs] = useState<LogModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [errorCodeFilter, setErrorCodeFilter] = useState<string>('');
  const [severityFilter, setSeverityFilter] = useState<string>('');

  const handleFetch = async () => {
    try {
      setLoader(true);
      const { errorLogs, totalPages } = await fetchErrorLogs(currentPage, {
        bookId,
        errorCode: errorCodeFilter || null,
        severity: severityFilter || null,
      });
      setErrorLogs(errorLogs);
      setTotalPages(totalPages);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    handleFetch();
  }, [currentPage, errorCodeFilter, severityFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page: number | any) => {
    if (page > 0 && page <= totalPages!) setCurrentPage(+page);
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[95vw] max-w-7xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-800">Error Logs</h2>
            <span className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
              Book ID: {bookId.substring(0, 16)}…
            </span>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={severityFilter}
              onChange={(e) => {
                setSeverityFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              title="Filter by severity"
            >
              {SEVERITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={errorCodeFilter}
              onChange={(e) => {
                setErrorCodeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
            >
              {ERROR_CODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleFetch}
              disabled={loader}
              className="inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded px-3 py-1.5 border border-gray-300 disabled:opacity-50 cursor-pointer"
              title="Refresh"
            >
              ↻ Refresh
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none font-light cursor-pointer px-1"
              title="Close (Esc)"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {loader ? (
            <LoadingSpinner />
          ) : (
            <>
              <ErrorLogsTable errorLogs={errorLogs} />
              <div className="flex justify-end mt-4 gap-2">
                {(totalPages ?? 0) > 1 && (
                  <PaginationButtons
                    totalPages={totalPages!}
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookErrorsModal;
